
import { isEmpty } from "lodash";
import {
  IAccount,
  INavigation,
  LEAD_TIME_FIELD_NAME,
  RSI_HOME_NAMES,
} from "@share/common-types";
import { LIFE_STYLE_NAME_PARAM, SESSION_KEY_TOKEN_NAME_PARAM } from "../constants/common";
import { UrlUtils } from "./urls";
import { NULL_VALUE } from "@constants";
import { LoginType } from "./login";

export const getPreferenceFromAccount = (account: IAccount | null | undefined, key: string, defaultValue?: string) => {
  if (!isEmpty(account?.preferences)) {
    const pref = account?.preferences.find(p => p.key === key);
    if (!isEmpty(pref?.value)) {
      return pref?.value;
    }
  }
  return !isEmpty(defaultValue) ? defaultValue : NULL_VALUE;
}

export const getPreferenceFileUrlFromAccount = (account: IAccount | null | undefined, key: string) => {
  const preference =  getPreferenceFromAccount(account, key);
  if (!isEmpty(preference)) {
    try {
      const preferenceObj = preference ? JSON.parse(preference) : preference;
      if (!isEmpty(preferenceObj?.Value)) {
        return preferenceObj.Value;
      }
    } catch (e) {
      console.error('Parsing JSON error')
    }
  }
  return NULL_VALUE;
}


export const getAccountLeadTime = (account: IAccount) => {
  const leadTimePref = getPreferenceFromAccount(account, LEAD_TIME_FIELD_NAME, '0');
    
  let leadTime = 0;
  try {
    leadTime = leadTimePref ? parseInt(leadTimePref) : 0;
  } catch(e) {
    leadTime = 0;
    console.error(e);
  }
  return leadTime;
}

export const IsClientCashEnabled = (account: IAccount | null | undefined) => {
  const isPublicAccountType = account?.type === LoginType.Public;
  const isPrivateWithLoginAccountType = account?.type === LoginType.PrivateWithLogin;
  const hasClientCash = account?.hasClientCash;

  return (isPublicAccountType || isPrivateWithLoginAccountType) && hasClientCash;
}

const GetPrivateTokenParam = (account: IAccount | null | undefined) => {
  const isPrivateWithTokenAccountType = account?.type === LoginType.PrivateWithToken;
  const privateTokenFromUrl = account?.privateTokenFromUrl;
  const privateTokenParam = `${(isPrivateWithTokenAccountType && privateTokenFromUrl) ? `${SESSION_KEY_TOKEN_NAME_PARAM}=${UrlUtils.getSearchParam(SESSION_KEY_TOKEN_NAME_PARAM)}` : ''}`;

  return privateTokenParam;
}

const GetLifeStyleParam = (account: IAccount | null | undefined) => {
  const hasClientCash = account?.hasClientCash;
  const lifeStyle = UrlUtils.getSearchParam(LIFE_STYLE_NAME_PARAM);
  const lifeStyleParam = `${(hasClientCash && !isEmpty(lifeStyle)) ? `${LIFE_STYLE_NAME_PARAM}=${lifeStyle}` : ''}`;

  return lifeStyleParam;
}

export const GetHomeParams = (account: IAccount | null | undefined) => {
  const privateTokenParam = GetPrivateTokenParam(account);
  const lifeStyleParam = GetLifeStyleParam(account);

  const hasPrivateTokenParam = !isEmpty(privateTokenParam);
  const hasLifeStyleParam = !isEmpty(lifeStyleParam);
  
  if (hasPrivateTokenParam && hasLifeStyleParam) {
    return `?${privateTokenParam}&${lifeStyleParam}`;
  } else if (hasPrivateTokenParam) {
    return `?${privateTokenParam}`;
  } else if (hasLifeStyleParam) {
    return `?${lifeStyleParam}`;
  } else {
    return '';
  }
}

export const GetWalletNumber = (value: number | null | undefined, account: IAccount | null | undefined) => {
  const balance = (value || value === 0) ? account?.walletNoDecimals ? Math.floor(value || 0) : Math.floor((value || 0) * 100) / 100 : null;
  return balance?.toLocaleString('en-US');
}

export const GetStyles = (
    account: IAccount | null | undefined,
    backgroundImage: any,
    borderRadius: string | null | undefined,
    rsiBackground: any,
    defaultBackground: any,
    isSearch: boolean,
    isExperiences: boolean | null | undefined,
    isWidget: boolean | null | undefined,
    widgetBackgroundColor: string | null | undefined,
    listLength: number | null | undefined) => {
  let withImage = false;
  const styleAccount: any = {};   
  const styleMain: any = {};   
  if (!isExperiences) {  
    if (isWidget && widgetBackgroundColor) {
      styleAccount.background = widgetBackgroundColor;
      styleMain.background = 'none';
    } else {
      const background = account?.backgroundColor;
      const logoImage = backgroundImage ? 
                          backgroundImage : 
                          RSI_HOME_NAMES.includes(account?.name || '')? 
                              rsiBackground : 
                              background ? 
                                  null : 
                                  isWidget ? null : defaultBackground;

      if (background && !logoImage) {
        styleAccount.background = background;
        styleMain.background = 'none';
      } 

      if (logoImage) {
        if (!isSearch) {
          withImage = true;
          styleMain.background = 'none';
        }

        styleAccount.backgroundImage = account?.isRSITemplate || listLength ?
          !isSearch && `url(${logoImage})` :
          isWidget ?
            !isSearch && `url(${logoImage})` :
            !isSearch && `linear-gradient(rgba(255,255,255,0) 80%, rgba(255,255,255,1)), url(${logoImage})`;
      }  
    }
  } 

  if (borderRadius && !isEmpty(borderRadius)) {
    try {
      const border = parseInt(borderRadius);
      styleAccount.borderRadius = `${border}px`;
    } catch(e) {
      console.error(e);
    }
  }

  return { styleAccount, styleMain, withImage };
}

const normalizeRecentSearchBlocklistIds = (raw?: (string | number)[] | null): string[] => {
  if (!raw?.length) return [];
  return raw
    .map((id) => String(id).trim())
    .filter((s) => s.length > 0);
};

const parseContextId = (id: number | string | null | undefined): string | null => {
  if (id == null) return null;
  const s = String(id).trim();
  return s.length > 0 ? s : null;
};

/**
 * Returns true when the recent-search preview image grid should be hidden for this org/site.
 * RSI only; `disableRecentSearchPreviewImagesRsi` turns previews off for everyone; org/site lists
 * are blocklists (only matching IDs are hidden). Each list is evaluated only when non-empty, with
 * normalized string ids so CSV/whitespace mismatches do not hide the feature incorrectly.
 */
export const computeIsRecentSearchPreviewImagesDisabled = (
  account: IAccount | null | undefined,
  siteId?: number | string | null,
  organizationId?: number | string | null,
): boolean => {
  const isDisabled = !!account?.disableRecentSearchPreviewImagesRsi;
  if (!isDisabled) {
    return false;
  }

  if (!account?.isRSITemplate) {
    return isDisabled;
  }
  
  const siteList = normalizeRecentSearchBlocklistIds(account?.disableRecentSearchPreviewImagesRsiSiteidList);
  const orgList = normalizeRecentSearchBlocklistIds(account?.disableRecentSearchPreviewImagesRsiOrgidList);

  if (siteList?.length === 0 && orgList?.length === 0) {
    return true;
  }

  const sid = parseContextId(siteId);
  const oid = parseContextId(organizationId);
  const excludedBySite = siteList?.length > 0 && sid != null && siteList.includes(sid);
  const excludedByOrg = orgList?.length > 0 && oid != null && orgList.includes(oid);

  return excludedBySite || excludedByOrg;
};

export const GetDisableNewTabOpen = (account: IAccount | null, items: INavigation | null) => {
  const isRSITemplate = account?.isRSITemplate;
  const disableNewTabOpenRsiSiteidList = account?.disableNewTabOpenRsiSiteidList;
  const disableNewTabOpenRsiOrgidList = account?.disableNewTabOpenRsiOrgidList;

  const siteId: any = items?.siteId;
  const organizationId: any = items?.organizationId;

  const emptyDisableNewTabOpenRsiSiteid = !disableNewTabOpenRsiSiteidList?.length;
  const emptyDisableNewTabOpenRsiOrgid = !disableNewTabOpenRsiOrgidList?.length;
  const emptyDisableNewTabOpen = emptyDisableNewTabOpenRsiSiteid && emptyDisableNewTabOpenRsiOrgid;

  const includedDisableNewTabOpenRsiSiteid = !emptyDisableNewTabOpenRsiSiteid ? disableNewTabOpenRsiSiteidList?.includes(siteId?.toString()) : false;
  const includedDisableNewTabOpenRsiOrgid = !emptyDisableNewTabOpenRsiOrgid ? disableNewTabOpenRsiOrgidList?.includes(organizationId?.toString()) : false;

  const disableNewTabOpenRsi = isRSITemplate && ((account?.disableNewTabOpen && emptyDisableNewTabOpen) || (account?.disableNewTabOpen && (includedDisableNewTabOpenRsiSiteid || includedDisableNewTabOpenRsiOrgid)))
  const disableNewTabOpenRest = !isRSITemplate && account?.disableNewTabOpen
  const disableNewTabOpen = disableNewTabOpenRsi || disableNewTabOpenRest;

  return disableNewTabOpen;
}

/** Credit line: preference on + private login/token only (not Public or API). */
export const isEnableCreditLine = (account: IAccount | null | undefined): boolean =>
  Boolean(account?.enableCreditLine) &&
  (account?.type === LoginType.PrivateWithLogin ||
    account?.type === LoginType.PrivateWithToken);

/** Alias for checkout UI and balance API eligibility. */
export const isCreditLineCheckoutAccount = (account: IAccount | null | undefined): boolean =>
  isEnableCreditLine(account);

/** Stripe express checkout: non-RSI uses account toggle only; RSI scopes by org/site lists (empty = all). */
export const computeIsStripePaymentMethodsEnabled = (
  account: IAccount | null | undefined,
  siteId?: number | string | null,
  organizationId?: number | string | null,
): boolean => {
  if (isEnableCreditLine(account)) {
    return false;
  }
  if (!account?.enableStripePaymentMethods) {
    return false;
  }
  if (!account.isRSITemplate) {
    return true;
  }
  const orgList = account.enableStripePaymentMethodsRsiOrgidList ?? [];
  const siteList = account.enableStripePaymentMethodsRsiSiteidList ?? [];
  if (!orgList.length && !siteList.length) {
    return true;
  }
  const sid = siteId != null ? String(siteId) : '';
  const oid = organizationId != null ? String(organizationId) : '';
  if (!sid && !oid) {
    return false;
  }
  return siteList.includes(sid) || orgList.includes(oid);
};

/** True if Stripe ExpressCheckoutElement's onReady `availablePaymentMethods` includes any wallet button. */
export const hasStripeExpressWalletMethods = (
  available: Record<string, boolean> | null | undefined,
): boolean => {
  if (!available || typeof available !== 'object') {
    return false;
  }
  return Object.values(available).some((v) => v === true);
};